/**
 * Branch
 *
 * Filename: branch.ts
 *
 * @category  Fahrradxxl
 * @author    Janik Kemnade <j.kemnade@fahrrad-xxl.de>
 * @copyright 2026 Fahrrad-xxl.de
 */



/* 📜 INFORMATION
 * --------------------------------------------------------------------------------------------------------------------
 * Haupt-Entrypoint für alles was mit Branches / Filialen zu tun hat.
 * Diese Datei soll zukünftig folgendes ersetzten:
 *  - 420_branch.js
 *  - 400_functions_and_variables.js - Funktionen und Variabeln die hier bzgl. Branch verortet wurden
 *
 */



/* 📑 TODO
 * --------------------------------------------------------------------------------------------------------------------
 * - Es gibt viele Stellen an denen Datensätze neu zusammengebunden werden,
 *   diese sollte zusammengefasst und vereinheitlichtwerden, am besten eigentlich via PHP:
 *      - custom/module/branch/branch_fc.php:154 -> getBranchFilterDataByBranchId
 *      - custom/module/intern/json_personal.php:208 -> getBranchFilterCookie
 *      - custom/module/public/fxxl3/assets/js/420_branch.js:449 -> fxxlSetFilterCookieForChosenBranch
 *      - custom/module/public/fxxl3/assets/js/module/branch_search_modal.js:156 -> setBranchButtonLink
 *
 * - In der 420_branch.js finden sich Variabeln und Funktionen die eigentlich mit Melibo zu tun haben, wieso?
 *   Beispiele:
 *      - fxxlBranchMeliboWidgetSelector
 *      - fxxlGetMeliboWidget
 *   Diese Dinge sollten eigentlich hier verwertet werden und stehen aktuell 420_branch.js quasi doppelt:
 *      - custom/module/public/fxxl3/assets/js/module/melibo.js:41
 *      - Beispiel: 420_branch.js -> fxxlBranchMeliboToggleSelectors und melibo.js -> widgetElementCSSSelectorToggleCandidates
 *
 * - In der 420_branch.js finden sich auch Funktionen die zu Allgemein für 420_branch.js wären.
 *   Diese sollten in FxxlCore aufgenommen werden. Beispiel:
 *      - fxxlIsElementVisible
 *
 * - Im aller besten Fall, ließen sich hier noch folgenden Datein über Sub-Klassen integrieren:
 *      - branch_search.js
 *      - branch_search_modal.js
 *
 */






/* 📦️ IMPORTS
 * ----------------------------------------------------------------------------------------------------------------- */
import FxxlCore from '@fxxl/core';
import FxxlError from '@fxxl/error';






/* 📑 ENUMS & TYPES & CONSTANTS
 * ----------------------------------------------------------------------------------------------------------------- */
// HTML Classes
enum HTMLCLASS {
    CHECKOUT2_DELIVERY_BRANCH_TITLE_DISTANCE = 'fxxl-warenkorb-checkout2__deliverybranch-info-title-distance',
}


// Selectors
const SELECTOR = {
    CHECKOUT2_JS_DELIVERY_BRANCH_SECTIONS:       `.js_checkout2__deliverybranch_section[data-checkout2-deliverybranch-id]`,
    CHECKOUT2_JS_DELIVERY_BRANCH_SECTION_TARGET: (branchId: number) => `.js_checkout2__deliverybranch_section[data-checkout2-deliverybranch-id="${branchId}"]`,
    CHECKOUT2_DELIVERY_BRANCH_TITLE_DISTANCE:    `.${HTMLCLASS.CHECKOUT2_DELIVERY_BRANCH_TITLE_DISTANCE}`,
};






/* 💾 INTERFACES
 * ----------------------------------------------------------------------------------------------------------------- */
export interface IFxxlLocalStorageBranchFilterData {
    branchId:       string
    branchOrt:      string
    branchParam:    string
    branchSource:   string
    branchDistance: number | null
}






/* ✒️ CLASS: FxxlBranch
 * ----------------------------------------------------------------------------------------------------------------- */
export default class FxxlBranch {

    /* 🏷️ VARS
     * ----------------------------------------------------------------------------------------- */
    private static LS_BRANCH_ID:        string = FxxlCore.getConfig().storageKeys.branchId;
    private static LS_BRANCH_ORT:       string = FxxlCore.getConfig().storageKeys.branchOrt;
    private static LS_BRANCH_PARAM:     string = FxxlCore.getConfig().storageKeys.branchParam;
    private static LS_BRANCH_SOURCE:    string = FxxlCore.getConfig().storageKeys.branchSource;
    private static LS_BRANCH_DISTANCE:  string = FxxlCore.getConfig().storageKeys.branchDistance;




    /* 🐙 STATIC SETS & GETS
     * ----------------------------------------------------------------------------------------- */
    /**
     * Set LocalStorage Branch Data Variables
     *
     * @param branchfilterdata
     */
    public static setLocalStorageBranchData(branchfilterdata: any): void {
        FxxlCore.setStorage(FxxlBranch.LS_BRANCH_ID,     branchfilterdata?.branch_id     || '');
        FxxlCore.setStorage(FxxlBranch.LS_BRANCH_ORT,    branchfilterdata?.branch_ort    || '');
        FxxlCore.setStorage(FxxlBranch.LS_BRANCH_PARAM,  branchfilterdata?.branch_param  || '');
        FxxlCore.setStorage(FxxlBranch.LS_BRANCH_SOURCE, branchfilterdata?.branch_source || '');
    }
    /**
     * Set LocalStorage Branch Data Variables - Distance
     *
     * @param branchDistance
     */
    public static setLocalStorageBranchDistance(branchDistance: number): void {
        FxxlCore.setStorage(FxxlBranch.LS_BRANCH_DISTANCE, branchDistance || null);
    }


    /**
     * Get LocalStorage Branch Data Variables
     *
     * @return IFxxlLocalStorageBranchFilterData | null
     */
    public static getLocalStorageBranchData(): IFxxlLocalStorageBranchFilterData | null {
        const branchId:         string = FxxlCore.getStorage(FxxlBranch.LS_BRANCH_ID)       || '';
        const branchOrt:        string = FxxlCore.getStorage(FxxlBranch.LS_BRANCH_ORT)      || '';
        const branchParam:      string = FxxlCore.getStorage(FxxlBranch.LS_BRANCH_PARAM)    || '';
        const branchSource:     string = FxxlCore.getStorage(FxxlBranch.LS_BRANCH_SOURCE)   || '';
        const branchDistance:   number | null = FxxlCore.getStorage(FxxlBranch.LS_BRANCH_DISTANCE) || null;

        if (branchId && branchOrt && branchParam && branchSource) {
            return {
                branchId,
                branchOrt,
                branchParam,
                branchSource,
                branchDistance,
            };
        }

        return null;
    }




    /* 🐙 Checkout 2
     * ----------------------------------------------------------------------------------------- */
    public static checkout2HandleSetBranchDelivery(
        targetElement: HTMLInputElement,
        branchId: number,
        branchDistance?: number,
        callback?: () => void
    ): void {
        const boxes = document.querySelectorAll(SELECTOR.CHECKOUT2_JS_DELIVERY_BRANCH_SECTIONS);
        boxes.forEach((box) => {
            box?.classList.add('fxxl-hidden');
        });

        const selectedBox = document.querySelector(SELECTOR.CHECKOUT2_JS_DELIVERY_BRANCH_SECTION_TARGET(branchId));
        if (selectedBox !== null) {
            selectedBox.classList.remove('fxxl-hidden');

            const distanceElement = selectedBox.querySelector(SELECTOR.CHECKOUT2_DELIVERY_BRANCH_TITLE_DISTANCE);
            if (branchDistance && distanceElement) {
                distanceElement.innerHTML = branchDistance + ' km';
            }
        }

        targetElement.value = String(branchId);
        if (callback) {
            callback();
        }
    }


}